import { Component, type ErrorInfo, type ReactNode } from "react";

interface Props {
  children: ReactNode;
  fallbackMessage?: string;
}

interface State {
  hasError: boolean;
  retryCount: number;
  lastError: string;
}

const MAX_AUTO_RETRIES = 3;
const RETRY_DELAY_MS = 1500;

/**
 * Global error boundary with self-healing:
 * - Catches React render crashes
 * - Auto-retries up to 3 times with exponential back-off
 * - Clears corrupted localStorage on repeated failures
 * - Shows minimal fallback UI only after exhausting retries
 */
export default class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, retryCount: 0, lastError: "" };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, lastError: error?.message || "Unknown error" };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("[ErrorBoundary] Caught:", error.message, info.componentStack);

    // On 2nd+ retry, attempt to clear potentially corrupted state
    if (this.state.retryCount >= 1) {
      this.healLocalStorage();
    }

    // Auto-retry with back-off
    if (this.state.retryCount < MAX_AUTO_RETRIES) {
      const delay = RETRY_DELAY_MS * Math.pow(2, this.state.retryCount);
      setTimeout(() => {
        this.setState((s) => ({
          hasError: false,
          retryCount: s.retryCount + 1,
        }));
      }, delay);
    }
  }

  /** Remove known app keys that could cause parse/render errors */
  private healLocalStorage() {
    const keysToHeal = [
      "llf_played_history",
      "llf_played_groups",
      "llf_implicit_groups",
      "llf_follow_track",
      "llf_debug_open",
    ];
    for (const key of keysToHeal) {
      try {
        const raw = localStorage.getItem(key);
        if (raw === null) continue;
        // Validate JSON keys
        if (key !== "llf_follow_track" && key !== "llf_debug_open") {
          JSON.parse(raw); // throws if corrupt
        }
      } catch {
        console.warn(`[SelfHeal] Removing corrupted key: ${key}`);
        try { localStorage.removeItem(key); } catch { /* noop */ }
      }
    }
  }

  render() {
    if (this.state.hasError && this.state.retryCount >= MAX_AUTO_RETRIES) {
      return (
        <div
          className="fixed inset-0 z-[10000] flex flex-col items-center justify-center bg-background text-foreground p-6 text-center"
        >
          <p className="font-serif text-lg mb-4">Something went wrong</p>
          <p className="text-sm text-muted-foreground mb-6 max-w-xs">
            The app ran into an issue. Tap below to reload.
          </p>
          <button
            onClick={() => window.location.reload()}
            className="px-6 py-2.5 rounded bg-absa-red text-white font-sans text-sm tracking-wide uppercase"
          >
            Reload
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
